import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatDate, formatApiError, statusLabel } from "../lib/format";
import { Building2, MapPin, Phone, Home, Calendar, ArrowRight, CheckCircle2, Loader2, Sparkles, MessageCircle, Clock, X } from "lucide-react";

// Normalises a phone to wa.me-friendly digits (E.164 without leading +).
const waDigits = (raw) => {
  if (!raw) return null;
  let d = String(raw).replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  // Nigerian local numbers: "08012345678" → "2348012345678"
  if (d.length === 11 && d.startsWith("0")) d = "234" + d.slice(1);
  return d || null;
};

const buildWaLink = (raw, prefilledText) => {
  const d = waDigits(raw);
  if (!d) return null;
  const t = encodeURIComponent(prefilledText || "");
  return `https://wa.me/${d}${t ? `?text=${t}` : ""}`;
};

export default function PublicHome() {
  const [settings, setSettings] = useState(null);
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [interest, setInterest] = useState(null);
  const [sent, setSent] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/public/settings").catch(() => ({ data: {} })),
      api.get("/public/vacancies").catch(() => ({ data: [] })),
    ]).then(([s, v]) => {
      setSettings(s.data); setVacancies(v.data); setLoading(false);
    });
  }, []);

  const available = useMemo(() => vacancies.filter(v => !v.is_upcoming), [vacancies]);
  const upcoming = useMemo(() => vacancies.filter(v => v.is_upcoming), [vacancies]);

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const f = new FormData(e.target);
    const name = f.get("name");
    const email = f.get("email");
    const phone = f.get("phone");
    const message = f.get("message");
    try {
      await api.post("/public/leads", { name, email, phone, message, interested_unit_id: interest });
      const unitLabel = interest && vacancies.find(v => v.id === interest)?.unit_name;
      setLastMessage({
        name, email, phone, message,
        waText: `Hello ${settings?.manager_name || "Modav Homes"}, I'm ${name}${unitLabel ? ` and I'm enquiring about ${unitLabel}` : ""}.\n\n${message}\n\nReply: ${email}${phone ? ` / ${phone}` : ""}`,
      });
      setSent(true);
      toast.success("Message sent! Continue on WhatsApp for an instant reply.");
      e.target.reset();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const logo = settings?.logo_url || "";

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F9F8]"><Loader2 className="animate-spin text-[#0E5C3A]" size={24}/></div>;

  const promoActive = settings?.promo_banner_active && !bannerDismissed && (settings?.promo_banner_title || settings?.promo_banner_body);
  const managerWa = buildWaLink(settings?.manager_whatsapp, "Hello Modav Homes, I'd like to enquire about a unit.");
  const adminWa = buildWaLink(settings?.admin_whatsapp, "Hello Modav Homes Admin, I'd like to enquire about a unit.");

  return (
    <div className="min-h-screen bg-[#F7F9F8]">
      {/* Promo Banner */}
      {promoActive && (
        <div className="bg-[#0E5C3A] text-white" data-testid="promo-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-2.5 flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles size={14} className="text-[#C9A227] shrink-0"/>
              <span className="font-bold truncate">{settings.promo_banner_title}</span>
              {settings.promo_banner_body && <span className="text-white/85 hidden sm:inline truncate">— {settings.promo_banner_body}</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {settings.promo_banner_cta_url && (
                <a href={settings.promo_banner_cta_url} className="bg-[#C9A227] text-[#1A2E24] font-bold px-3 py-1 rounded text-xs hover:bg-[#D9B247]" data-testid="promo-banner-cta">
                  {settings.promo_banner_cta_label || "Learn more"} →
                </a>
              )}
              <button onClick={() => setBannerDismissed(true)} className="text-white/70 hover:text-white" aria-label="Dismiss" data-testid="promo-banner-dismiss"><X size={14}/></button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-[#E5EBE8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#0E5C3A] rounded-xl p-1.5"><img src={logo} alt="Modav" className="w-9 h-9 object-contain"/></div>
            <div>
              <div className="font-serif font-bold text-[#0E5C3A] text-lg leading-tight">Modav Homes</div>
              <div className="text-[10px] italic text-[#C9A227] tracking-wide">Comfort & Convenience</div>
            </div>
          </div>
          <nav className="flex items-center gap-1 sm:gap-3 text-sm">
            <a href="#vacancies" className="btn-ghost hidden sm:inline-flex">Vacancies</a>
            <a href="#contact" className="btn-ghost hidden sm:inline-flex">Contact</a>
            <Link to="/login" className="btn-outline-emerald text-sm" data-testid="sign-in-link">Tenant Sign In</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A4A2E] to-[#0E5C3A]"></div>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 0%, rgba(201,162,39,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.1) 0%, transparent 50%)" }}></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-white">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-xs uppercase tracking-[0.2em] text-[#C9A227] font-bold mb-4"><Sparkles size={12}/> Ilese-Ijebu, Ogun State</div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1]">A home where<br/><span className="text-[#C9A227] italic">comfort meets convenience.</span></h1>
              <p className="mt-6 text-white/80 text-lg max-w-md">Modern shops, halls, self-contained rooms, room & parlour and two-bedroom flats — fully serviced, secured and managed.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#vacancies" className="btn-gold" data-testid="hero-vacancies-btn">See Vacancies <ArrowRight size={14}/></a>
                {managerWa && (
                  <a href={managerWa} target="_blank" rel="noreferrer" className="btn-outline-emerald !bg-white/10 !border-white !text-white hover:!bg-white/20" data-testid="hero-whatsapp-manager">
                    <MessageCircle size={14}/> WhatsApp Manager
                  </a>
                )}
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                <Stat number={`${available.length}`} label="Available Now"/>
                <Stat number={`${upcoming.length}`} label="Soon (≤60d)"/>
                <Stat number="24/7" label="Management"/>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 p-8 fade-up delay-2">
                <img src={logo} alt="Modav" className="w-32 h-32 mx-auto bg-white rounded-2xl p-3"/>
                <div className="mt-6 text-center space-y-1">
                  <div className="font-serif text-2xl font-bold">Modav Homes Estate</div>
                  <div className="text-sm text-white/70 flex items-center justify-center gap-1"><MapPin size={12}/> {settings?.property_address || "Ilese-Ijebu, Ogun State"}</div>
                  <div className="text-sm text-white/70">Landlord: <b className="text-[#C9A227]">{settings?.landlord_name || "Mr. Seun Odusanya"}</b></div>
                  <div className="text-sm text-white/70">Manager: <b className="text-[#C9A227]">{settings?.manager_name || "Arch. Keshinro"}</b></div>
                  {settings?.manager_phone && (
                    <div className="text-sm text-white/70 flex items-center justify-center gap-1"><Phone size={12}/> {settings.manager_phone}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vacancies */}
      <section id="vacancies" className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 space-y-12">
          <VacancyBlock
            eyebrow="Available Now"
            title="Open Vacancies"
            subtitle="Reserve your unit. Pricing is transparent and lease terms are 12 months."
            units={available}
            emptyTitle="No units available right now"
            emptyBody="All units are currently occupied. Send a message below to be added to our waiting list — we'll contact you the moment something opens up."
            onEnquire={(id) => { setInterest(id); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
          />

          {upcoming.length > 0 && (
            <VacancyBlock
              eyebrow="Coming Up · Within 60 Days"
              title="Available Soon"
              subtitle="These leases are ending shortly and have no renewal on file. Reserve early to secure your spot."
              units={upcoming}
              emptyTitle=""
              emptyBody=""
              onEnquire={(id) => { setInterest(id); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
            />
          )}
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white border-y border-[#E5EBE8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 grid md:grid-cols-3 gap-8">
          <FeatureCard icon={Building2} title="Mixed-use Estate" body="From commercial shops & a large hall to residential rooms & flats — one address, many uses."/>
          <FeatureCard icon={CheckCircle2} title="Transparent Pricing" body="Annual rent + caution, agreement, service and commission — all stated upfront, no surprises."/>
          <FeatureCard icon={Home} title="Modern Management" body="Digital lease, online portal for tenants, 24/7 maintenance ticketing, monthly statements."/>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-bold">Get in touch</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24] mt-2">Talk to our property manager</h2>
            <p className="text-[#5E7166] mt-2">Send us a message and {settings?.manager_name || "Arch. Keshinro"} will reach out within 24 hours — or continue the conversation on WhatsApp.</p>
          </div>

          {sent ? (
            <div className="card-modav p-8 sm:p-10 text-center" data-testid="contact-sent">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F5EE] text-[#0E5C3A] flex items-center justify-center mb-4"><CheckCircle2 size={28}/></div>
              <h3 className="font-serif text-2xl font-bold">Thank you!</h3>
              <p className="text-[#5E7166] mt-2">Your enquiry has been delivered to {settings?.manager_name || "our team"}. For an instant reply, continue on WhatsApp below — your message is already pre-filled.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {managerWa ? (
                  <a href={buildWaLink(settings.manager_whatsapp, lastMessage?.waText)} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center" data-testid="continue-whatsapp-manager">
                    <MessageCircle size={14}/> WhatsApp {settings.manager_name?.split(" ")[0] || "Manager"}
                  </a>
                ) : (
                  <div className="text-xs text-[#5E7166] italic">Manager WhatsApp not configured</div>
                )}
                {adminWa ? (
                  <a href={buildWaLink(settings.admin_whatsapp, lastMessage?.waText)} target="_blank" rel="noreferrer" className="btn-outline-emerald w-full justify-center" data-testid="continue-whatsapp-admin">
                    <MessageCircle size={14}/> WhatsApp Admin
                  </a>
                ) : null}
              </div>
              <button onClick={() => { setSent(false); setLastMessage(null); }} className="btn-ghost mt-5 mx-auto text-xs">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submitLead} className="card-modav p-6 sm:p-8 space-y-4" data-testid="contact-form">
              {interest && vacancies.find(v => v.id === interest) && (
                <div className="bg-[#FDF6E3]/40 border border-[#EBDDA7] rounded-lg p-3 text-sm flex items-center justify-between">
                  <span>Enquiring about <b>{vacancies.find(v => v.id === interest)?.unit_name}</b></span>
                  <button type="button" onClick={() => setInterest(null)} className="text-xs text-[#5E7166] hover:text-[#1A2E24]">Clear</button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Your name</span>
                  <input name="name" required className="input-modav mt-1" data-testid="lead-name"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Email</span>
                  <input name="email" type="email" required className="input-modav mt-1" data-testid="lead-email"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Phone (optional)</span>
                <input name="phone" className="input-modav mt-1" placeholder="+234 ..." data-testid="lead-phone"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Message</span>
                <textarea name="message" required rows={4} className="input-modav mt-1" placeholder="Tell us a bit about what you're looking for and when you'd like to move in." data-testid="lead-message"/></label>
              <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="lead-submit">{submitting ? <><Loader2 className="animate-spin" size={14}/> Sending…</> : "Send Message"}</button>
              <div className="text-xs text-[#5E7166] text-center pt-1">After submitting you can continue the chat on WhatsApp with one tap.</div>
            </form>
          )}

          <div className="mt-10 grid sm:grid-cols-2 gap-4 text-sm text-[#5E7166]">
            <div className="card-modav p-4 flex items-start gap-3">
              <MapPin size={18} className="text-[#0E5C3A] flex-shrink-0 mt-0.5"/>
              <div>
                <div className="font-semibold text-[#1A2E24]">Location</div>
                {settings?.property_address || "Along Ondo/Benin Road, Opposite Ogun State Polytechnic of Health and Allied Sciences, Ilese-Ijebu, Ogun State, Nigeria"}
              </div>
            </div>
            <div className="card-modav p-4 flex items-start gap-3" data-testid="manager-card">
              <Building2 size={18} className="text-[#0E5C3A] flex-shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A2E24]">Property Manager</div>
                <div>{settings?.manager_name || "Arch. Keshinro"}</div>
                {settings?.manager_phone && (
                  <a href={`tel:${settings.manager_phone}`} className="text-[#0E5C3A] hover:underline inline-flex items-center gap-1 mt-1 text-xs" data-testid="manager-phone-link">
                    <Phone size={12}/> {settings.manager_phone}
                  </a>
                )}
                {managerWa && (
                  <a href={managerWa} target="_blank" rel="noreferrer" className="ml-3 text-[#0E5C3A] hover:underline inline-flex items-center gap-1 mt-1 text-xs" data-testid="manager-whatsapp-link">
                    <MessageCircle size={12}/> WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5EBE8] py-8 text-center text-xs text-[#5E7166]">
        © {new Date().getFullYear()} Modav Homes · Comfort & Convenience · Ilese-Ijebu, Ogun State
      </footer>
    </div>
  );
}

function VacancyBlock({ eyebrow, title, subtitle, units, emptyTitle, emptyBody, onEnquire }) {
  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-bold">{eyebrow}</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24] mt-2">{title}</h2>
          <p className="text-[#5E7166] mt-1 max-w-md">{subtitle}</p>
        </div>
      </div>
      {units.length === 0 ? (
        emptyTitle ? (
          <div className="card-modav p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#E8F5EE] text-[#0E5C3A] mx-auto mb-3 flex items-center justify-center"><Home size={22}/></div>
            <h3 className="font-serif text-xl font-bold">{emptyTitle}</h3>
            <p className="text-[#5E7166] mt-2 max-w-md mx-auto">{emptyBody}</p>
          </div>
        ) : null
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {units.map(v => (
            <div key={v.id} className="card-modav p-6 fade-up" data-testid={`vacancy-${v.unit_number}`}>
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#C9A227] font-bold">{v.unit_type}</div>
                  <h3 className="font-serif text-xl font-bold mt-1 truncate">{v.unit_name}</h3>
                  <div className="text-xs text-[#5E7166]">#{v.unit_number} · {v.use_type}</div>
                </div>
                <span className={v.is_upcoming ? "badge badge-gold" : (v.status === "vacant" ? "badge badge-green" : "badge badge-gold")}>
                  {v.is_upcoming ? <span className="inline-flex items-center gap-1"><Clock size={10}/> Soon</span> : statusLabel(v.status)}
                </span>
              </div>
              <div className="space-y-1.5 text-sm border-t border-[#E5EBE8] pt-3 mt-3">
                <div className="flex justify-between"><span className="text-[#5E7166]">Rent (annual)</span><span className="naira-total">{naira(v.suggested_rent)}</span></div>
                <div className="flex justify-between"><span className="text-[#5E7166]">Caution fee</span><span>{naira(v.caution_fee)}</span></div>
                <div className="flex justify-between"><span className="text-[#5E7166]">Agreement fee</span><span>{naira(v.agreement_fee)}</span></div>
                <div className="flex justify-between"><span className="text-[#5E7166]">Service charge</span><span>{naira(v.service_charge)}</span></div>
                <div className="flex justify-between pt-1 border-t border-[#E5EBE8]"><span className="text-[#5E7166] flex items-center gap-1"><Calendar size={12}/> {v.availability_label || "Available from"}</span><span className="font-semibold">{formatDate(v.available_from)}</span></div>
              </div>
              <button onClick={() => onEnquire(v.id)} className="btn-gold w-full justify-center mt-4" data-testid={`enquire-${v.unit_number}`}>
                {v.is_upcoming ? "Reserve early" : "Enquire about this"} <ArrowRight size={14}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
      <div className="font-serif text-2xl font-bold text-[#C9A227]">{number}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/70 mt-1">{label}</div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#E8F5EE] text-[#0E5C3A] mx-auto flex items-center justify-center mb-3"><Icon size={24}/></div>
      <h3 className="font-serif text-lg font-bold">{title}</h3>
      <p className="text-sm text-[#5E7166] mt-1">{body}</p>
    </div>
  );
}
